import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

const SelectComponent = ({
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  style = {},
  optionStyle = {},
  dropdownStyle = {}
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelectClick = () => {
    setIsOpen(!isOpen);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen(!isOpen);
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleOptionClick = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const selectedOption = options.find(option => option.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  const defaultSelectStyle = {
    position: 'relative',
    display: 'inline-block',
    minWidth: '328px',
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: '4px',
    cursor: 'pointer',
    userSelect: 'none',
    ...style
  };

  const selectBoxStyle = {
    padding: 8,
    paddingRight: isMobile ? 40 : 35,
    fontSize: 14,
    minHeight: isMobile ? '48px' : 'auto',
    display: 'flex',
    alignItems: 'center',
    color: '#333',
    backgroundColor: '#fff',
    border: 'none',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box'
  };

  const arrowStyle = {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: `translateY(-50%) ${isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}`,
    transition: 'transform 0.2s ease',
    pointerEvents: 'none',
    width: '0',
    height: '0',
    borderLeft: '4px solid transparent',
    borderRight: '4px solid transparent',
    borderTop: '6px solid #999',
  };

  const defaultDropdownStyle = {
    position: 'absolute',
    top: '100%',
    left: '0',
    right: '0',
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderTop: 'none',
    borderRadius: '0 0 4px 4px',
    maxHeight: '300px',
    overflowY: 'auto',
    zIndex: 1000,
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    ...dropdownStyle
  };

  const defaultOptionStyle = {
    padding: isMobile ? '16px 12px' : '12px 8px',
    fontSize: isMobile ? '14px' : '14px',
    minHeight: isMobile ? '48px' : '40px',
    backgroundColor: '#fff',
    color: '#333',
    cursor: 'pointer',
    borderBottom: '1px solid #f0f0f0',
    display: 'flex',
    alignItems: 'center',
    transition: 'background-color 0.2s ease',
    ...optionStyle
  };

  return (
    <div ref={selectRef} style={defaultSelectStyle}>
      <div
        style={selectBoxStyle}
        onClick={handleSelectClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        {displayText}
      </div>
      <div style={arrowStyle} />
      {isOpen && (
        <div style={defaultDropdownStyle}>
          {options.map(option => (
            <div
              key={option.value}
              style={{
                ...defaultOptionStyle,
                backgroundColor: option.value === value ? '#f5f5f5' : '#fff'
              }}
              onClick={() => handleOptionClick(option.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleOptionClick(option.value);
                }
              }}
              role="option"
              aria-selected={option.value === value}
              tabIndex={0}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f0f0f0';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = option.value === value ? '#f5f5f5' : '#fff';
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

SelectComponent.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  placeholder: PropTypes.string,
  style: PropTypes.object,
  optionStyle: PropTypes.object,
  dropdownStyle: PropTypes.object,
};

export default SelectComponent;



// WEBPACK FOOTER //
// ./src/components/SelectComponent.js